import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  AccessGrantSchema,
  AccessGrantCreateInputSchema,
  AccessGrantUpdateInputSchema,
  type AccessGrant,
  type AccessGrantCreateInput,
  type AccessGrantUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `accessGrant` (autorisation d'accès, org-wide).
 *
 * Calque le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`AccessGrantCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 *
 * Note sécurité : le secret `credentialCode` n'est PAS porté par ce document v0
 * (sous-document restreint ou hash, hors v0) ; il ne transite donc pas par ce
 * repository.
 */

export type { AccessGrantCreateInput };

/** Nom de la collection Firestore de la famille accessGrant. */
export const ACCESS_GRANTS_COLLECTION = 'accessGrants';

/**
 * Repository typé pour la famille `accessGrant`.
 * Toute lecture est validée par `AccessGrantSchema`, toute écriture par
 * `AccessGrantCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface AccessGrantRepository {
  create(input: AccessGrantCreateInput): Promise<AccessGrant>;
  list(options?: ListOptions): Promise<AccessGrant[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<AccessGrant | null>;
  update(id: string, patch: AccessGrantUpdateInput): Promise<AccessGrant>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<AccessGrant>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<AccessGrant>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<AccessGrant>;
}

const accessGrantConverter = createConverter<AccessGrant>((raw: DocumentData) => AccessGrantSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createAccessGrantRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): AccessGrantRepository {
  const col = collection(db, ACCESS_GRANTS_COLLECTION).withConverter(accessGrantConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<AccessGrant>(db, col, ACCESS_GRANTS_COLLECTION, getActorId);

  return {
    async create(input: AccessGrantCreateInput): Promise<AccessGrant> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = AccessGrantCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.placeId !== undefined) optional['placeId'] = fields.placeId;
      if (fields.resourceId !== undefined) optional['resourceId'] = fields.resourceId;
      if (fields.portalRef !== undefined) optional['portalRef'] = fields.portalRef;
      if (fields.validFrom !== undefined) optional['validFrom'] = fields.validFrom;
      if (fields.validUntil !== undefined) optional['validUntil'] = fields.validUntil;
      if (fields.revokedAt !== undefined) optional['revokedAt'] = fields.revokedAt;
      if (fields.subscriptionId !== undefined) optional['subscriptionId'] = fields.subscriptionId;
      if (fields.loanId !== undefined) optional['loanId'] = fields.loanId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        granteeContactId: fields.granteeContactId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as AccessGrant);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return AccessGrantSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<AccessGrant[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<AccessGrant | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: AccessGrantUpdateInput): Promise<AccessGrant> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = AccessGrantUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, ACCESS_GRANTS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`accessGrant ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
